// De subtaken per huishouden: welk werk er bij een huishouden ligt, los van de
// afspraak waar het onder hangt.
//
// WAAROM APART VAN openTasksPerAdress: die telt ze, dit levert ze. Alledrie de
// plekken die subtaken van een huishouden willen — de kolom TAKEN, het tabblad
// 'Subtaken' op het huishouden en het lijstje onder de details van een afspraak
// — moeten het huishouden van een subtaak op DEZELFDE manier vinden, en dat is
// niet triviaal (zie hieronder). Twee keer uitgeschreven lopen ze uiteen.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
//
// Wat een subtaak IS staat niet hier maar in taskKind.js: sinds 2026-09-30 is
// het een eigen soort (`addressSubtask`) dat ook zonder afspraak kan bestaan.
// Tot de omzetting gedraaid heeft staan beide vormen naast elkaar.
// AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md

import { isAppointment, isSubtask } from './taskKind'

/** De standen waarin een taak nog werk is. */
const OPEN_STATUSES = ['toDo', 'inProgress']

/** Wat de gebruiker leest bij een stand. */
const STATUS_LABELS = { toDo: 'Te doen', inProgress: 'Bezig', done: 'Klaar' }

/**
 * Of deze subtaak nog gedaan moet worden.
 *
 * Een subtaak zonder stand telt als 'Te doen'. Dat volgt het scherm, dat hem
 * ook zo toont (`t.Status || 'toDo'` in SubTasks.jsx); hem hier als klaar
 * rekenen zou werk laten verdwijnen dat de begeleider wél voor zich ziet.
 *
 * @param {{Status?: string}} subtask
 * @returns {boolean}
 */
const isOpenSubtask = (subtask) => {
    const status = subtask?.Status
    if (!status) return true
    return OPEN_STATUSES.includes(status)
}

/**
 * @param {{Status?: string}} subtask
 * @returns {string} De stand in gewone taal
 */
const subtaskStatusLabel = (subtask) => STATUS_LABELS[subtask?.Status || 'toDo'] || 'Te doen'

/**
 * De subtaken per huishouden, elk met de afspraak waar hij onder hangt.
 *
 * Een subtaak hangt aan zijn huishouden via zijn eigen `AdressID`, en anders
 * via de hoofdafspraak waar hij onder hangt: 22 van de 109 subtaken in
 * productie dragen dat veld niet zelf, en die zouden zonder die tweede weg stil
 * niet meetellen — een huishouden zou dan minder werk tonen dan er ligt.
 *
 * De volgorde blijft die van de invoer; de aanroeper sorteert zelf.
 *
 * @typedef {object} HouseholdSubtask
 * @property {boolean} planned - Of de subtaak aan een afspraak hangt; `false` = nog niet ingepland
 * @property {string} parentId - `ID` van de afspraak waar de subtaak onder hangt; `''` als hij nog niet ingepland is
 * @property {boolean} parentFound - Of die afspraak nog bestaat. Alleen zinvol als `planned` waar is: een niet-ingeplande subtaak heeft geen afspraak om te vinden
 * @property {string} parentTitle - Titel van die afspraak; '' als die er niet is
 * @property {*} parentDate - `Timestamp` van die afspraak, of null
 *
 * @param {Array<object>} [tasks] - Alle Tasks-documenten van de organisatie
 * @returns {Record<string, Array<object & HouseholdSubtask>>} Huishoudens zonder subtaken ontbreken
 */
const subtasksPerAdress = (tasks) => {
    const alle = Array.isArray(tasks) ? tasks : []

    // De hoofdafspraken op ID, om het huishouden van een subtaak te vinden én
    // om te kunnen zeggen bij welke afspraak hij hoort.
    // Alleen echte afspraken: een niet-ingeplande subtaak heeft óók geen Parent,
    // maar is geen afspraak waar iets onder kan hangen.
    const hoofdtaken = new Map()
    alle.forEach((task) => {
        if (!task?.ID || !isAppointment(task)) return
        hoofdtaken.set(task.ID, task)
    })

    /** @type {Record<string, Array<object>>} */
    const result = {}

    alle.forEach((task) => {
        if (!isSubtask(task)) return
        if (task.Deleted === true) return

        const planned = Boolean(task.Parent)
        const ouder = planned ? (hoofdtaken.get(task.Parent) || null) : null
        const adressId = task.AdressID || ouder?.AdressID || ''
        if (!adressId) return

        if (!result[adressId]) result[adressId] = []
        result[adressId].push({
            ...task,
            planned,
            parentId: task.Parent || '',
            // Een subtaak kan zijn afspraak overleven: bij één huishouden in
            // productie waren alledrie de afspraken weg terwijl het werk er nog
            // lag. Die subtaken horen zichtbaar te blijven -- het werk bestaat
            // nog -- maar het scherm moet kunnen zeggen waaróm er geen afspraak
            // bij staat, anders leest een streepje als een storing.
            parentFound: Boolean(ouder),
            parentTitle: ouder?.Title || '',
            parentDate: ouder?.Timestamp || null,
        })
    })

    return result
}

export { OPEN_STATUSES, STATUS_LABELS, isOpenSubtask, subtaskStatusLabel }
export default subtasksPerAdress
